import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Empty, Select, type SelectProps, Spin, Tooltip } from "antd";
import debounce from "lodash/debounce";
import { type ApiEmpresaConveniadaSearchParams, apiEmpresaConveniadaSearch } from "pages/empresas_conveniadas/api/apiEmpresaConveniadaSearch";
import type { ApiEmpresaConveniada } from "pages/empresas_conveniadas/types/ApiEmpresaConveniada";
import { type ReactNode, useMemo, useState } from "react";
import { formatDocument } from "std/format";

const QTDE_MIN_CHARS = 3;
const debounceTimeout = 600;

export type TSelectEmpresaConveniada = Omit<SelectProps, "onChange"> & {
	onChange?: (value: number, empresaConveniada: ApiEmpresaConveniada | undefined) => void;
};

export function SelectEmpresaConveniada({ placeholder, onChange, onClear, ...rest }: TSelectEmpresaConveniada) {
	const [searchValue, setSearchValue] = useState("");

	const params: ApiEmpresaConveniadaSearchParams = useMemo(
		() => ({
			descricao: searchValue,
		}),
		[searchValue],
	);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ["apiEmpresaConveniadaSearch", params],
		queryFn: () => apiEmpresaConveniadaSearch({ params }),
		placeholderData: keepPreviousData,
	});

	const clear = (): void => {
		setSearchValue("");
	};

	const handleSearch = useMemo(() => {
		const loadOptions = (value: string) => {
			setSearchValue(value);

			if (value.length < QTDE_MIN_CHARS) {
				return;
			}

			refetch();
		};

		return debounce(loadOptions, debounceTimeout);
	}, [refetch]);

	const handleChange = (value: number): void => {
		const empresaConveniada = (data || []).find((c) => c.for_idpk === value);

		if (onChange) {
			onChange(value, empresaConveniada || undefined);
		}
	};

	const handleClear = (): void => {
		if (onClear) {
			onClear();
		}

		clear();
	};

	const renderNotFoundData = (): ReactNode => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

	const selectOptions = (data || []).map((item) => (
		<Select.Option key={item.for_idpk} value={item.for_idpk} label={item?.pessoa?.pes_nome_razaosocial || ""} className="zebra">
			<Tooltip placement="topLeft" title={item?.pessoa?.pes_nome_razaosocial || ""}>
				<div>
					{item?.pessoa?.pes_nome_fantasia && item?.pessoa?.pes_nome_fantasia !== item?.pessoa?.pes_nome_razaosocial && (
						<>
							<div style={{ fontWeight: "bold" }}>{item?.pessoa?.pes_nome_razaosocial}</div>
							<div style={{ fontSize: "13px", opacity: "0.7" }}>Nome Fantasia: {item?.pessoa?.pes_nome_fantasia}</div>
						</>
					)}
					{(!item?.pessoa?.pes_nome_fantasia || item?.pessoa?.pes_nome_fantasia === item?.pessoa?.pes_nome_razaosocial) && (
						<div style={{ fontWeight: "bold" }}>{item?.pessoa?.pes_nome_razaosocial}</div>
					)}
					{item?.pessoa?.pes_cnpj_cpf && (
						<div style={{ fontSize: "13px", opacity: "0.7" }}>CNPJ/CPF: {formatDocument(item?.pessoa?.pes_cnpj_cpf)}</div>
					)}
					{item?.for_idpk && <div style={{ fontSize: "13px", opacity: "0.7" }}>Código: {item?.for_idpk.toString()}</div>}
				</div>
			</Tooltip>
		</Select.Option>
	));

	return (
		<Select
			allowClear={true}
			showSearch={true}
			placeholder={placeholder || "Selecione uma Empresa Conveniada"}
			filterOption={false}
			onSearch={handleSearch}
			onChange={handleChange}
			onClear={handleClear}
			optionLabelProp="label"
			notFoundContent={isFetching ? <Spin size="small" /> : renderNotFoundData()}
			{...rest}
		>
			{selectOptions}
		</Select>
	);
}
